/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { ProductData, Product } from '@ikea-aoa/component-view-3d';
import {
	FunctionIcon,
	CoverItem,
	ImageItem,
	RangeSettings,
	Settings,
	Platform,
	TranslationsKey,
	IModuleSwiperItem,
} from 'sofa-reborn-react';
import { SofaIcons } from '../../typings/enums';
import { ProductUtility } from '@inter-ikea-kompis/utilities';
import { localeService } from '../../services';
import { ProductUtils } from '../../util/ProductUtils';
import { CheckoutAvailabilityService, LocalizationService } from '@inter-ikea-kompis/services';
import { IStoreAvailability, IProductItem } from '@inter-ikea-kompis/types';
import {
	StatusIndicatorUtility,
	StatusIndicatorColorEnum,
} from '@inter-ikea-kompis/component-status-indicator';

function getFunctionIcon(modelId: string, rangeSettings: RangeSettings): FunctionIcon | null {
	if (
		rangeSettings.modelsWithStorageFunctions &&
		rangeSettings.modelsWithStorageFunctions.includes(modelId)
	) {
		return {
			title: localeService.getTranslation('sprGalleryFunctionStorage'),
			icon: SofaIcons.FunctionStorageIcon,
			type: 'storage',
		};
	} else if (
		rangeSettings.modelsWithSleepFunction &&
		rangeSettings.modelsWithSleepFunction.includes(modelId)
	) {
		return {
			title: localeService.getTranslation('sprGalleryFunctionSleep'),
			icon: SofaIcons.FunctionSleepIcon,
			type: 'sleep',
		};
	}
	return null;
}

function getStoreAvailabilityId(product: Product): string {
	if (product.localId) {
		return product.localId;
	} else if (product.sprIdInvalid && product.sprItems) {
		return product.sprItems[0].localId || product.sprItems[0].itemno;
	}
	return product.productId;
}

async function getStoreAvailabilities(
	products: ProductData,
	settings: Settings,
	storeId: string | undefined,
	colorId: string,
	modulesInSwiper: string[]
): Promise<IStoreAvailability[] | null> {
	let storeAvailability: IStoreAvailability[] | null = null;
	const storeAvailabilityDataSource = localeService.getLocalizationSetting(
		'storeAvailabilityDataSource'
	);
	if (storeId && storeAvailabilityDataSource !== 'disabled') {
		const checkoutAvailabilityService = new CheckoutAvailabilityService({
			locale: settings.locale,
			localizationEndpoints: settings.localizationEndpoints,
		});
		const productItems: IProductItem[] = modulesInSwiper.reduce((productItems, modelId) => {
			const product = ProductUtils.getProduct(products, modelId, colorId);
			if (product) {
				productItems.push({
					id: getStoreAvailabilityId(product),
					type: product.type,
				});
			}
			return productItems;
		}, [] as IProductItem[]);
		try {
			storeAvailability = await checkoutAvailabilityService.getStoreAvailabilities(
				storeId,
				productItems
			);
		} catch (e) {
			// eslint-disable-next-line no-console
			console.warn('Could not get storeAvailability information for module swiper.', e);
		}
	}
	return storeAvailability;
}

export async function parseModuleSwiperItems(
	products: ProductData,
	productInfo: any,
	selectedCover: CoverItem,
	rangeSettings: RangeSettings,
	platform: Platform,
	useMetricMeasures: boolean,
	settings: Settings,
	storeId: string | undefined,
	storeName: string
): Promise<IModuleSwiperItem[]> {
	const moduleItems = new Array<IModuleSwiperItem>();
	const colorId = ProductUtils.getColorIdFromProduct(products, selectedCover.articleId);
	if (colorId) {
		const modulesInSwiper =
			platform === 'kiosk' ? rangeSettings.modulesInSwiperKiosk : rangeSettings.modulesInSwiper;
		let storeAvailabilities;
		let localizedInformation;
		if (storeId && !selectedCover.isIkeaPlus) {
			storeAvailabilities = await getStoreAvailabilities(
				products,
				settings,
				storeId,
				colorId,
				modulesInSwiper
			);
			const localizationService = new LocalizationService({
				locale: settings.locale,
				localizationPrefixApplication: settings.localizationPrefixApplication,
				localizationEndpoints: settings.localizationEndpoints,
			});
			localizedInformation = await localizationService.getLocalizedInformation();
		}
		modulesInSwiper.forEach((modelId) => {
			const productId = ProductUtils.getProductId(products, modelId, colorId);
			if (!productId) {
				return;
			}

			let info: any | null = null;
			const sprItems = products[productId].sprItems;
			if (productInfo[productId] && productInfo[productId].content) {
				info = productInfo[productId].content;
			} else if (sprItems && productInfo[sprItems[0].itemno]) {
				info = productInfo[sprItems[0].itemno].content;
			}
			if (!info) {
				return;
			}
			const imgSrc = colorId
				? rangeSettings.thumbnailsSrc + '/' + modelId + '_' + colorId.toUpperCase() + '.png'
				: '';
			const functionIcon = getFunctionIcon(modelId, rangeSettings);

			const productName =
				rangeSettings.modelsTranslationKeys && rangeSettings.modelsTranslationKeys[modelId]
					? localeService.getTranslation(
							rangeSettings.modelsTranslationKeys[modelId] as TranslationsKey
					  )
					: info.typeName;

			const moduleSwiperItem = {
				id: productId,
				productName: productName as any,
				measurements: '',
				imgSrc: imgSrc,
				disabled: false,
			};
			if (functionIcon) {
				moduleSwiperItem['functionIcon'] = functionIcon;
			}

			if (
				rangeSettings.modulesToShowMeasurements?.includes(modelId) &&
				productInfo[productId]?.content
			) {
				moduleSwiperItem['measurements'] = useMetricMeasures
					? productInfo[productId].content.measureReference.textMetric
					: productInfo[productId].content.measureReference.textImperial;
			}

			if (storeId) {
				if (storeAvailabilities && storeAvailabilities.length > 0) {
					const storeAvailabilityId = getStoreAvailabilityId(products[productId]);
					const storeAvailability = storeAvailabilities.find(
						(storeAvailabilitie) => storeAvailabilitie.itemId.split('-')[1] === storeAvailabilityId
					);
					if (storeAvailability && localizedInformation) {
						const storeAvailabilityLabel = StatusIndicatorUtility.getStoreAvailabilityLabel({
							localizedInformation,
							storeAvailability,
						});

						if (storeAvailabilityLabel) {
							moduleSwiperItem['stockStatus'] = {
								statusLabel: storeAvailabilityLabel.replace('[[store]]', storeName),
								statusColor: StatusIndicatorUtility.getStoreAvailabilityColor(storeAvailability),
							};
						}
					} else {
						moduleSwiperItem['stockStatus'] = {
							statusLabel: localeService.getTranslation('contactCoWorker'),
							statusColor: StatusIndicatorColorEnum.grey,
						};
					}
				} else if (selectedCover.isIkeaPlus) {
					moduleSwiperItem['stockStatus'] = {
						statusLabel: localeService.getTranslation('stockStatusLabelIkeaPlus'),
						statusColor: StatusIndicatorColorEnum.grey,
					};
				} else {
					moduleSwiperItem['stockStatus'] = {
						statusLabel: localeService.getTranslation('contactCoWorker'),
						statusColor: StatusIndicatorColorEnum.grey,
					};
				}
			}

			moduleItems.push(moduleSwiperItem);
		});
	}
	return moduleItems;
}

export function parseCoverItems(
	productsInfo: any,
	products: ProductData,
	rangeSettings: RangeSettings
): CoverItem[] {
	const coverItems = new Array<CoverItem>();
	const validProducts = Array.from(Object.values(products)).reduce(
		(acc: string[], item: Product) => {
			acc.push(item.productId);
			if (item.localId && item.localId !== item.productId) {
				acc.push(item.localId);
			}
			if (item.sprItems && item.sprItems.length) {
				item.sprItems.forEach((i) => acc.push(i.itemno));
			}
			return acc;
		},
		[]
	);
	rangeSettings.coverItems.forEach((coverItem) => {
		const coverItemArticleId = coverItem.articleIds.find((articleId: string) => {
			return productsInfo[articleId] && (validProducts as string[]).includes(articleId);
		});

		if (!coverItemArticleId) {
			return;
		}

		const product = productsInfo[coverItemArticleId].content;
		const images = new Array<ImageItem>();
		const name = product.validDesignText;
		let count = 0;

		if (product.image) {
			product.image.forEach((productImage): void => {
				// Limit to three images
				if (productImage.size === 'S4' && count < 3) {
					images.push({
						src: productImage.url,
						alt: '',
						productImage: true,
						typeName: productImage.typeName,
					});
					count++;
				}
			});
		}

		const productDescription = product.customerBenefit
			? product.customerBenefit.map((customerBenefit) => {
					return customerBenefit.text;
			  })
			: null;
		const careInstructions = product.careInstruction
			? product.careInstruction.map((careInstruction) => {
					return careInstruction.text;
			  })
			: null;
		const materialDescription = product.customerMaterial
			? product.customerMaterial.map((customerMaterial) => {
					return customerMaterial;
			  })
			: null;

		const countryCode = localeService.getCountryCode();
		const localSofabedMattressId = rangeSettings.localSofabedMattressId
			? rangeSettings.localSofabedMattressId[countryCode]
			: null;
		const newLocalSofabedMattressId = rangeSettings.newLocalSofabedMattressId
			? rangeSettings.newLocalSofabedMattressId[countryCode]
			: null;
		const globalSofabedMattressId = rangeSettings.globalSofabedMattressId
			? (rangeSettings.globalSofabedMattressId as string[])
			: [];
		const sofabedMattressId = rangeSettings.sofabedMattressId as string;

		const itemNo = ProductUtils.getIkeaProductId(
			product,
			localSofabedMattressId,
			newLocalSofabedMattressId,
			globalSofabedMattressId,
			sofabedMattressId,
			rangeSettings.useLocalIds?.includes(countryCode) as boolean,
			rangeSettings.localIds ? rangeSettings.localIds[countryCode] : null
		);
		const articleId = itemNo.toString();
		const articleIdIkea = product.itemNoLocal || product.itemNoGlobal;
		const articleIdText = ProductUtility.formatItemNo(articleIdIkea);
		const cover = {
			articleId,
			articleIdText,
			name,
			images,
			color: coverItem.color,
			coverName: coverItem.coverName,
			borderColor: coverItem.borderColor,
			productDescription,
			materialDescription,
			careInstructions,
			isRemovable: coverItem.isRemovable,
			isIkeaPlus: coverItem.isIkeaPlus,
			leg: coverItem.leg,
		} as CoverItem;
		coverItems.push(cover);
	});
	return coverItems;
}
